import { AppIcon, type AppIcons, FilterSelect } from '@backrs_ui/common-ui';

import { Box, MenuItem, type SxProps, type Theme, Typography } from '@mui/material';

export type FilterDropdownOption = Readonly<{ value: string; label: string }>;

type FilterDropdownBaseProps = Readonly<{
  id: string;
  label: string;
  options: FilterDropdownOption[];
  startIconName?: AppIcons;
}>;

type SingleFilterDropdownProps = Readonly<{
  multi: false;
  selectedValue: string | null;
  onChange: (value: string | null) => void;
  isClearable?: boolean;
}>;

type FilterDropdownProps = FilterDropdownBaseProps &
  Readonly<
    | {
        multi?: true;
        selectedValues: string[];
        onChange: (values: string[]) => void;
      }
    | SingleFilterDropdownProps
  >;

type FilterRenderValueProps = Readonly<{
  label: string;
  hasSelection: boolean;
  startIconName?: AppIcons;
}>;

const FilterRenderValue = ({ label, hasSelection, startIconName }: FilterRenderValueProps) => {
  const iconColor = hasSelection ? 'Colors/Text/text-primary' : 'Colors/Text/text-placeholder';

  return (
    <Box sx={renderValueStyle}>
      {startIconName && (
        <Box sx={renderValueIconStyle}>
          <AppIcon iconName={startIconName} diameter="20px" color={iconColor} />
        </Box>
      )}
      <Typography variant="text-md-medium" sx={renderValueLabelSx(hasSelection)}>
        {label}
      </Typography>
      <Box sx={renderValueIconStyle}>
        <AppIcon iconName="ChevronDown" diameter="20px" color={iconColor} />
      </Box>
    </Box>
  );
};

export const FilterDropdown = (props: FilterDropdownProps) => {
  const { id, label, options, startIconName } = props;

  if (props.multi === false) {
    const { selectedValue, onChange, isClearable = true } = props;
    const hasSelection = selectedValue !== null;
    const displayLabel = hasSelection
      ? (options.find((o) => o.value === selectedValue)?.label ?? label)
      : label;

    return (
      <FilterSelect
        id={id}
        displayEmpty
        renderValue={() => (
          <FilterRenderValue
            label={displayLabel}
            hasSelection={hasSelection}
            startIconName={startIconName}
          />
        )}
        value={selectedValue ?? ''}
        onChange={(value) => onChange(value === '' ? null : value)}
      >
        {isClearable ? (
          <MenuItem id={`${id}-option-empty`} value="" disableRipple>
            <Box sx={menuItemContentStyle}>
              <Box sx={menuItemIconStyle(!hasSelection)}>
                <AppIcon
                  iconName={!hasSelection ? 'Check' : 'Square'}
                  diameter="16px"
                  color={!hasSelection ? 'Colors/Foreground/fg-white' : 'currentColor'}
                />
              </Box>
              <Typography variant="text-md-medium">{`All ${label.toLowerCase()}s`}</Typography>
            </Box>
          </MenuItem>
        ) : null}
        {options.map((option) => {
          const isSelected = selectedValue === option.value;

          return (
            <MenuItem
              id={`${id}-option-${option.value}`}
              key={option.value}
              value={option.value}
              disableRipple
            >
              <Box sx={menuItemContentStyle}>
                <Box sx={menuItemIconStyle(isSelected)}>
                  <AppIcon
                    iconName={isSelected ? 'Check' : 'Square'}
                    diameter="16px"
                    color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                  />
                </Box>
                <Typography variant="text-md-medium" sx={menuItemLabelSx}>
                  {option.label}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </FilterSelect>
    );
  }

  const { selectedValues, onChange } = props;
  const hasSelection = selectedValues.length > 0;
  const displayLabel = hasSelection ? `${label} (${selectedValues.length})` : label;

  return (
    <FilterSelect
      multiple
      id={id}
      displayEmpty
      renderValue={() => (
        <FilterRenderValue
          label={displayLabel}
          hasSelection={hasSelection}
          startIconName={startIconName}
        />
      )}
      value={selectedValues}
      onChange={onChange}
    >
      {options.map((option) => {
        const isSelected = selectedValues.includes(option.value);

        return (
          <MenuItem
            id={`${id}-option-${option.value}`}
            key={option.value}
            value={option.value}
            disableRipple
          >
            <Box sx={menuItemContentStyle}>
              <Box sx={menuItemIconStyle(isSelected)} id={`${id}-checkbox-${option.value}`}>
                <AppIcon
                  iconName={isSelected ? 'Check' : 'Square'}
                  diameter="16px"
                  color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                />
              </Box>
              <Typography variant="text-md-medium" sx={menuItemLabelSx}>
                {option.label}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
    </FilterSelect>
  );
};

const renderValueStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  boxSizing: 'border-box',
  width: '100%',
  marginRight: backrs.spacing['spacing-md'],
});

const renderValueIconStyle: SxProps<Theme> = {
  width: '20px',
  height: '20px',
};

const renderValueLabelSx =
  (hasSelection: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: hasSelection
      ? backrs.colors['Colors/Text/text-primary']
      : backrs.colors['Colors/Text/text-placeholder'],
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  });

const menuItemContentStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const menuItemIconStyle =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const menuItemLabelSx: SxProps<Theme> = () => ({
  textTransform: 'capitalize',
});
